import React, { useState, useEffect } from 'react';
import { Avatar, CircularProgress } from '@mui/material';
import { getVimeoThumbnailUrl, isVimeoUrl } from 'utils/vimeo-utils';
import { MonitorIcon } from 'assets/icons';

interface VimeoThumbnailProps {
  url: string;
  width?: number;
  height?: number;
  sx?: any;
  fallbackIcon?: React.ReactNode;
}

const VimeoThumbnail: React.FC<VimeoThumbnailProps> = ({
  url,
  width = 40,
  height = 40,
  sx = {},
  fallbackIcon,
}) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchThumbnail = async () => {
      if (!isVimeoUrl(url)) {
        setError(true);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(false);
        const result = await getVimeoThumbnailUrl(url);
        if (result.success && result.thumbnailUrl) {
          setThumbnailUrl(result.thumbnailUrl);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Error fetching Vimeo thumbnail:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchThumbnail();
  }, [url]);

  if (loading) {
    return (
      <Avatar
        sx={{
          width,
          height,
          backgroundColor: 'background.default',
          borderColor: 'primary.light',
          borderStyle: 'solid',
          borderWidth: 1,
          ...sx,
        }}
      >
        <CircularProgress size={16} />
      </Avatar>
    );
  }

  if (error || !thumbnailUrl) {
    return (
      <Avatar
        sx={{
          width,
          height,
          backgroundColor: 'background.default',
          borderColor: 'primary.light',
          borderStyle: 'solid',
          borderWidth: 1,
          ...sx,
        }}
      >
        {fallbackIcon || <MonitorIcon sx={{ fontSize: 16 }} />}
      </Avatar>
    );
  }

  return (
    <Avatar
      src={thumbnailUrl}
      sx={{
        width,
        height,
        ...sx,
      }}
    >
      {fallbackIcon || <MonitorIcon sx={{ fontSize: 16 }} />}
    </Avatar>
  );
};

VimeoThumbnail.defaultProps = {
  width: 40,
  height: 40,
  sx: {},
  fallbackIcon: undefined,
};

export default VimeoThumbnail;
